import { useState, useLayoutEffect } from "react";

import { Button, LoadingSpinner } from "@newsela/angelou";
import PropTypes from "prop-types";

import globalToasterRef from "@/base/globalToasterRef";
import GradeRangeSelector from "@/components/GradeRangeSelector";
import SubjectSelector from "@/components/SubjectSelector";
import FIELD_DISABLED_HELPER_TEXT from "@/directory/constants/FIELD_DISABLED_HELPER_TEXT";
import useTeacherLanguageMutation from "@/teacher/hooks/useTeacherLanguageMutation";
import useTeacherSubjectMutation from "@/teacher/hooks/useTeacherSubjectMutation";
import useTeacherUpdate from "@/teacher/hooks/useTeacherUpdate";
import TeacherLanguageSelector from "@/teacher/TeacherLanguageSelector";
import {
  EVENT_TOAST_FAILED_UPDATED,
  EVENT_TOAST_SUCCESS_UPDATED,
  getHasFormChanged,
  getValidForm,
  GRADES,
  SUBJECTS,
} from "./TeacherClassroomProfile.utils";
import {
  containerCss,
  gradeSelectionCss,
  saveButtonCss,
  savingLoadingCss,
  submitContainerCss,
} from "./TeacherClassroomProfile.style";

const propTypes = {
  /**
   * Teacher object
   */
  teacher: PropTypes.shape({
    id: PropTypes.number.isRequired,
    highest_preferred_grade_level: PropTypes.number,
    lowest_preferred_grade_level: PropTypes.number,
    _editable: PropTypes.shape({
      highest_preferred_grade_level: PropTypes.bool,
      lowest_preferred_grade_level: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  /**
   * Callback function sending new teacher object
   */
  onTeacherUpdated: PropTypes.func.isRequired,
};

const TeacherClassroomProfile = ({
  teacher: initialTeacher,
  onTeacherUpdated,
}) => {
  const {
    response: { data: subjectData, loading: isLoadingSubjects },
    response: { data: { subjects: initialSubjects = [] } = {} },
    postMethod: updateSubjects,
  } = useTeacherSubjectMutation(initialTeacher.id);
  const {
    response: { data: languageData, loading: isLoadingLanguages },
    response: { data: { languages: initialLanguages = [] } = {} },
    postMethod: updateLanguages,
  } = useTeacherLanguageMutation(initialTeacher.id);
  const [updateTeacher] = useTeacherUpdate(initialTeacher.id);
  const [teacher, setTeacher] = useState(initialTeacher);
  const [subjects, setSubjects] = useState(initialSubjects);
  const [languages, setLanguages] = useState(initialLanguages);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidGrades, setIsValidGrades] = useState();
  const isValidForm = getValidForm({ isValidGrades, subjects });
  const hasFormChanged = getHasFormChanged(
    [initialTeacher, initialSubjects, initialLanguages],
    [teacher, subjects, languages],
  );
  const isLoading = isLoadingSubjects || isLoadingLanguages;

  useLayoutEffect(() => {
    setTeacher(initialTeacher);
  }, [initialTeacher]);

  useLayoutEffect(() => {
    setSubjects(initialSubjects);
  }, [subjectData]);

  useLayoutEffect(() => {
    setLanguages(initialLanguages);
  }, [languageData]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const responses = await Promise.all([
      updateTeacher(teacher),
      updateSubjects({ subjects }),
      updateLanguages({ languages }),
    ]);
    const hasError = responses.some((response) => response.error);
    if (!responses[0].error) await onTeacherUpdated(responses[0].data);
    globalToasterRef.current.displayToast(
      hasError ? EVENT_TOAST_FAILED_UPDATED : EVENT_TOAST_SUCCESS_UPDATED,
    );
    setIsSaving(false);
  };

  const onChangeGrades = ({ lowest, highest }) => {
    setTeacher({
      ...teacher,
      lowest_preferred_grade_level: lowest,
      highest_preferred_grade_level: highest,
    });
  };

  const onClickCheckbox = (setStateFn, value, checked) => {
    if (checked) {
      setStateFn((items) => [...items, value]);
    } else {
      setStateFn((items) => items.filter((item) => item !== value));
    }
  };

  if (isLoading && !isSaving) return <LoadingSpinner />;

  return (
    <form role="form" css={containerCss}>
      <GradeRangeSelector
        grades={{
          highest: teacher.highest_preferred_grade_level,
          lowest: teacher.lowest_preferred_grade_level,
        }}
        setIsValid={setIsValidGrades}
        setGrades={onChangeGrades}
        itemToString={(key) => GRADES[key] || ""}
        __css={gradeSelectionCss}
        disabledMessage={
          teacher._editable.highest_preferred_grade_level &&
          teacher._editable.lowest_preferred_grade_level
            ? ""
            : FIELD_DISABLED_HELPER_TEXT
        }
        lowestGradeProps={{ label: "Lowest grade" }}
        highestGradeProps={{ label: "Highest grade" }}
      />
      <SubjectSelector
        title="Primary content needed"
        subjects={SUBJECTS.map((subject) => ({
          ...subject,
          checked: subjects.includes(subject.name),
        }))}
        onSubjectsChange={(name, _, checked) =>
          onClickCheckbox(setSubjects, name, checked)
        }
      />
      <TeacherLanguageSelector
        title="Content languages needed"
        languages={languages}
        onClick={(key, checked) => onClickCheckbox(setLanguages, key, checked)}
      />
      <div css={submitContainerCss}>
        <Button
          flavor={Button.flavors.primary}
          type="submit"
          onClick={onSubmit}
          disabled={!isValidForm || !hasFormChanged || isSaving}
          __cssFor={saveButtonCss}
        >
          Save changes
        </Button>
        {isSaving && <LoadingSpinner __cssFor={savingLoadingCss} />}
      </div>
    </form>
  );
};

TeacherClassroomProfile.propTypes = propTypes;

export default TeacherClassroomProfile;
